/** Bounded, inert schema metadata for connector discovery (never example payloads). */
export const CONNECTOR_ASYNC_EXTENSION_KEYS = [
  "x-ms-trigger",
  "x-ms-trigger-hint",
  "x-ms-notification-content",
  "x-ms-long-running-operation",
  "x-ms-long-running-operation-options",
] as const;

export function safeConnectorSchemaText(value: string): string {
  return value
    .replace(/\bBearer\s+[A-Za-z0-9._~+/-]+={0,2}/gi, "Bearer [REDACTED]")
    .replace(/\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g, "[REDACTED]")
    .replace(
      /((?:access[_-]?token|refresh[_-]?token|id[_-]?token|client[_-]?secret|api[_-]?key|password|authorization)["']?\s*[:=]\s*["']?)[^\s,"'&}]+/gi,
      "$1[REDACTED]",
    )
    .replace(/([?&](?:token|secret|password|sig|key|api[_-]?key)=)[^&\s]+/gi, "$1[REDACTED]")
    .replace(/[\p{Cc}\p{Cf}]/gu, " ");
}

/** Resolve local definition refs only; retain unresolved/cyclic refs and mark every limit. */
export function boundedConnectorSchema(
  value: unknown,
  definitions: Record<string, unknown> = {},
  options: { preserveRootKeys?: boolean } = {},
): unknown {
  let remaining = 600;
  const ancestors = new WeakSet<object>();
  const visit = (node: unknown, depth: number, refs: Set<string>, propertyMap = false): unknown => {
    if (--remaining < 0 || depth > 8) return "[schema truncated]";
    if (typeof node === "string") {
      const safe = safeConnectorSchemaText(node);
      return safe.length > 1200 ? `${safe.slice(0, 1200)} [truncated]` : safe;
    }
    if (node === null || typeof node === "boolean" || typeof node === "number") return node;
    if (!node || typeof node !== "object") return undefined;
    if (ancestors.has(node)) return "[cyclic schema]";
    ancestors.add(node);
    try {
      if (Array.isArray(node)) {
        const result = node.slice(0, 40).map((item) => visit(item, depth + 1, refs));
        if (node.length > 40) result.push("[schema truncated]");
        return result;
      }
      const record = node as Record<string, unknown>;
      const ref = record["$ref"];
      if (typeof ref === "string" && !refs.has(ref)) {
        const match = /^#\/definitions\/([^/]+)$/.exec(ref);
        const key = match?.[1]?.replace(/~1/g, "/").replace(/~0/g, "~");
        if (key && Object.hasOwn(definitions, key)) {
          return visit(definitions[key], depth + 1, new Set([...refs, ref]));
        }
      }
      const result: Record<string, unknown> = {};
      const entries = Object.entries(record);
      for (const [key, item] of entries.slice(0, 60)) {
        if (["__proto__", "prototype", "constructor"].includes(key)) continue;
        if (!propertyMap && ["example", "examples", "default"].includes(key)) continue;
        const safeKey = safeConnectorSchemaText(key).slice(0, 160);
        result[safeKey] =
          /^(?:access[_-]?token|refresh[_-]?token|id[_-]?token|client[_-]?secret|api[_-]?key|password|authorization)$/i.test(
            key,
          ) && typeof item === "string"
            ? "[REDACTED]"
            : visit(item, depth + 1, refs, key === "properties" || key === "headers");
        if (remaining < 0) break;
      }
      if (entries.length > 60 || remaining < 0) result["[schema truncated]"] = true;
      return result;
    } finally {
      ancestors.delete(node);
    }
  };
  return visit(value, 0, new Set(), options.preserveRootKeys ?? false);
}

/** Preserve only known async extensions actually supplied at this exact Swagger location. */
export function connectorAsyncExtensions(
  value: unknown,
  definitions: Record<string, unknown> = {},
): Record<string, unknown> | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
  const record = value as Record<string, unknown>;
  const result = Object.fromEntries(
    CONNECTOR_ASYNC_EXTENSION_KEYS.filter((key) => Object.hasOwn(record, key)).map((key) => [
      key,
      boundedConnectorSchema(record[key], definitions),
    ]),
  );
  return Object.keys(result).length ? result : undefined;
}
